import type {
  Perspective,
  PerspectiveTableData,
  PerspectiveTable,
  PerspectiveWorker,
  PerspectiveDomElement,
  PerspectiveColumn,
} from "@/models/perspective";
import type { ColumnarTraces } from "@/models/traces";
import { difference } from "lodash";

// Perspective table data cannot be stored using Vue's reactivity because
// it has to be cloneable to Apache Arrow formatting. We, therefore, store
// the data here in a cache that is not reactive and instead will have to
// be refreshed manually as needed. Note that perspective tables have their
// own reactivity.
const rawTablesData: Record<string, PerspectiveTableData> = {};

const tables: Record<string, PerspectiveTable> = {};

const dynamicColumnLists: Record<string, Array<string>> = {};

export type PerspectiveIdentifiers =
  | "traces"
  | "spans"
  | "logs"
  | "metrics"
  | "docs"
  | "test";

const mainWorkers: Record<
  PerspectiveIdentifiers,
  undefined | PerspectiveWorker
> = {} as Record<PerspectiveIdentifiers, undefined | PerspectiveWorker>;
const secondaryWorkers: Record<
  PerspectiveIdentifiers,
  undefined | PerspectiveWorker
> = {} as Record<PerspectiveIdentifiers, undefined | PerspectiveWorker>;

/**
 * Instantiates the perspective workers used to display the perspective table
 * and to translate the data to JSON format
 */
export function initWorkers(): void {
  const identifiers: PerspectiveIdentifiers[] = [
    "traces",
    "spans",
    "logs",
    "metrics",
    "docs",
  ];
  for (const identifier of identifiers) {
    mainWorkers[identifier] = window.perspective.worker();
    secondaryWorkers[identifier] = window.perspective.worker();
  }
}

/**
 * Return the associated secondary worker to a given identifier
 * @param identifier String identifier used to select the correct worker.
 * @returns PerspectiveWorker worker process initialized
 */
export function getSecondaryWorker(
  identifier: PerspectiveIdentifiers
): PerspectiveWorker {
  return secondaryWorkers[identifier] as PerspectiveWorker;
}

/**
 * Attach a perspective table to its dom element.
 * @param identifier - Unique identifier for the table to attach to the given
 *   element.
 * @param element - A perspective-viewer web component element.
 * @param config - Optional table state from save() to apply to the table.
 */
export async function attachTableTo(
  identifier: PerspectiveIdentifiers,
  element: PerspectiveDomElement,
  config: Record<string, any> = {}
): Promise<void> {
  const table = getTable(identifier);
  element.load(table);
  await element.restore(config);
}

/**
 * Checks the incoming Array of columns to see if any are new.
 * @param identifier - - Unique identifier of the table.
 * @param columns - The incoming list of columns to check.
 * @returns Array of any new column names. Empty array if there are no new columns.
 */
export function checkNewColumns(
  identifier: PerspectiveIdentifiers,
  columns: Array<string>
): Array<string> {
  const existingDynamicColumns: Array<string> =
    dynamicColumnLists[identifier] || [];
  return columns.filter((col) => !existingDynamicColumns.includes(col));
}

/**
 * Clear the table data for the table with the given identifier.
 * @param identifier - Unique identifier of the table to be clear.
 * @returns table object if exists, or undefined otherwise.
 */
export function clearTable(
  identifier: PerspectiveIdentifiers
): PerspectiveTable | undefined {
  if (tables[identifier]) {
    rawTablesData[identifier] = {};
    tables[identifier].clear();
  }
  return tables[identifier];
}

/**
 * Clear all tables and reset schemas.
 */
export function clearPerspective(): boolean {
  Object.keys(tables).forEach((key) => {
    deleteTable(<PerspectiveIdentifiers>key);
    delete rawTablesData[key];
    delete dynamicColumnLists[key];
  });
  return true;
}

/**
 * Create perspective table data storage.
 * @param identifier - Unique identifier for the table.
 * @param tableSchema - The schema for the table.
 * @param tableData - Data to be displayed in the table.`
 * @param tableSchema - The base schema for the data. If not provided, a
 *   default for the identifier from currentSchemas or {} will be used.
 * @param perspective - Injectable global perspective library object. If not
 *   specified directly, it will be fetched from the window object.
 * @returns table perspective object to load into the desired DOM element, and the schema used to create it.
 */
export async function createTable(
  identifier: PerspectiveIdentifiers,
  tableSchema: Record<string, string>,
  tableData: PerspectiveTableData,
  perspective: Perspective | null = null
): Promise<[PerspectiveTable, Record<string, string>]> {
  const worker: PerspectiveWorker = (
    perspective
      ? perspective.worker()
      : mainWorkers[identifier] !== undefined
      ? mainWorkers[identifier]
      : window.perspective.worker()
  ) as PerspectiveWorker;
  const dynamicColumns =
    (tableData as Record<string, Array<any>>).dynamicColumns || [];
  const newColumnNames = dynamicColumns.map((column) => column.name) || [];
  const newSchema = handleSchema(identifier, tableSchema, newColumnNames);
  const updatedTableData = handleDynamicColumns(
    identifier,
    newSchema,
    tableData
  );
  const table = await worker.table(newSchema);
  table.update(updatedTableData || {});
  rawTablesData[identifier] = updatedTableData;
  tables[identifier] = table;
  return [table, newSchema];
}

/**
 * Removes the table data storage for the table with the given identifier.
 * @param identifier - Unique identifier of the table to be removed.
 * @returns boolean indicating whether table data existed and was deleted.
 */
export function deleteTable(identifier: PerspectiveIdentifiers): boolean {
  const table = tables[identifier];
  setTimeout(() => {
    if (table !== undefined) {
      table.delete();
    }
  }, 2000);
  delete tables[identifier];
  delete rawTablesData[identifier];
  return true;
}

/**
 * Retrieve stored perspective table data for the given identifier.
 * @param identifier - Unique identifier for the table in which to store data.
 * @returns PerspectiveTableData for the specified table, or undefined if the
 *   table does not exist.
 */
export function getTableData(
  identifier: PerspectiveIdentifiers
): PerspectiveTableData | undefined {
  return rawTablesData[identifier];
}

/**
 * Update the tableData object with names and values of DynamicColumns.
 * The Array of DynamicColumns gets flattened to match other columns then gets removed.
 * @param identifier - Unique identifier of the table to look up.
 * @param schema - The updated schema for the table.
 * @param tableData - Data to be transformed and passed to the table.
 * @returns - The new data with dynamic columns added and boolean representing whether
 *  the schema changed.
 */
export function handleDynamicColumns(
  identifier: PerspectiveIdentifiers,
  schema: Record<string, string>,
  tableData: PerspectiveTableData
): PerspectiveTableData {
  const updatedData = { ...(tableData as Record<string, any>) };
  const dynamicColumns = (tableData as ColumnarTraces).dynamicColumns || [];
  if (dynamicColumns !== undefined) {
    for (const column of dynamicColumns) {
      if (Object.keys(schema).includes(column.name))
        updatedData[column.name] = column.values;
    }
  }
  delete updatedData.dynamicColumns;
  return updatedData as PerspectiveTableData;
}

/**
 * Uses the incoming tableData to find dynamic columns. Adds them to the
 * schema.
 * @param identifier - Unique identifier of the table to look up.
 * @param schema - The current schema to be updated with new columns.
 * @param dynamicColumns - Incoming dynamic columns to check.
 * @returns The updated schema.
 */
export function handleSchema(
  identifier: PerspectiveIdentifiers,
  schema: Record<string, string>,
  dynamicColumns: Array<string>
): Record<string, string> {
  const newSchema = { ...schema };
  const newColumns = checkNewColumns(identifier, dynamicColumns);
  const existingColumns = dynamicColumnLists[identifier] || [];
  if (!newColumns) return newSchema;
  const union = existingColumns.concat(newColumns);
  union.map((column) => (newSchema[column] = "string"));
  dynamicColumnLists[identifier] = union;
  return newSchema;
}

/**
 * Retrieve store perspective table object associated with the given identifier.
 * @param identifier - Unique identifier of the table to look up.
 * @returns table object if exists, or undefined otherwise.
 */
export function getTable(
  identifier: PerspectiveIdentifiers
): PerspectiveTable | undefined {
  return tables[identifier];
}

/**
 * Delete and create the table at the identifier using existing schema.
 * The schema will be updated as data is added to the table. However,
 * the table needs to be deleted and recreated to apply a new schema.
 * @param identifier - Unique identifier of the table to look up.
 * @param schema - The schema for the table.
 * @param newColumns - The array of new column names, if any.
 * @param tableData - Data to be transformed and passed to the table.
 *  If not provided, the table will be created with the pre-existing raw table data.
 * @param mockPerspective - Variable used to mock the table view returned by the worker.
 * @returns - Return a new Perspective table with the data of the deleted table and the schema.
 */
export async function recreateTable(
  identifier: PerspectiveIdentifiers,
  schema: Record<string, string>,
  newColumns: Array<string> = [],
  tableData: PerspectiveTableData | null = null,
  mockPerspective: Perspective | null = null
): Promise<[PerspectiveTable, Record<string, string>]> {
  const data: PerspectiveTableData =
    tableData || getTableData(identifier) || {};
  deleteTable(identifier);
  const newSchema = handleSchema(identifier, schema, newColumns);
  return await createTable(identifier, newSchema, data, mockPerspective);
}

/**
 * Find keys that exist already in the table but are not included in this
 * update. Those will need to be automatically populated with null columns.
 * @param existingKeys - Set of keys that already exist in the table.
 * @param updateKeys - Set of keys that are included in this data update.
 * @returns Set of keys not found in the update that are in the table.
 */
function findMissingKeys(existingKeys: Set<string>, updateKeys: Set<string>) {
  const missingKeys = new Set(existingKeys);
  updateKeys.forEach((k) => missingKeys.delete(k));
  return missingKeys;
}

/**
 * Find keys that are not yet in the table but are included in this update.
 * These keys will have to be pre-populated as null columns in the table for
 * the append operation to work correctly.
 * @param existingKeys - Set of keys that already exist in the table.
 * @param updateKeys - Set of keys that are included in this data update.
 * @returns Set of keys in the update that are not already in the table.
 */
function findNewKeys(existingKeys: Set<string>, updateKeys: Set<string>) {
  const newKeys = new Set(updateKeys);
  existingKeys.forEach((k) => newKeys.delete(k));
  return newKeys;
}

/**
 * Update the table data for the table with the given identifier.
 * @param identifier - Unique identifier of the table to look up.
 * @param schema - The schema for the table.
 * @param tableData - Data that is passed to the table.
 * @returns Array of new columns found. Can be an empty array.
 */
export function updateTableData(
  identifier: PerspectiveIdentifiers,
  schema: Record<string, string>,
  tableData: PerspectiveTableData
): Array<string> {
  const dynamicColumns =
    (tableData as Record<string, Array<any>>).dynamicColumns || [];
  const newColumnNames = dynamicColumns.map((column) => column.name) || [];
  const newSchema = handleSchema(identifier, schema, newColumnNames);
  const updatedTableData = handleDynamicColumns(
    identifier,
    newSchema,
    tableData
  );

  // Get the existing table data that we're going to append to.
  const existing = rawTablesData[identifier] ?? {};

  // List all table keys by combining existing ones with new ones potentially
  // added by this update operation. __typename should not be included in keys.
  const existingKeys = new Set(Object.keys(existing));
  existingKeys.delete("__typename");
  const updateKeys = new Set(Object.keys(updatedTableData));
  updateKeys.delete("__typename");
  const allKeys = new Set([...existingKeys, ...updateKeys]);
  const missingKeys = findMissingKeys(existingKeys, updateKeys);
  const newKeys = findNewKeys(existingKeys, updateKeys);
  const sharedKeys = new Set([...existingKeys, ...updateKeys]);
  missingKeys.forEach((k) => sharedKeys.delete(k));
  newKeys.forEach((k) => sharedKeys.delete(k));

  // We need to determine how many records are being added by this update
  // operation so that any columns missing records can be filled with nulls.
  const pagedSize =
    updateKeys.size > 0 ? updatedTableData[[...updateKeys][0]].length : 0;

  const tableSize =
    sharedKeys.size > 0
      ? rawTablesData[identifier][[...sharedKeys][0]].length
      : 0;

  // Populate a new stored table that contains all existing data for the table
  // along with all new data that has been specified by this update operation.
  // Any columns not included in this updated page of data will be filled with
  // an array of null values to preserve column lengths.
  const combined: PerspectiveTableData = {};
  allKeys.forEach((key) => {
    if (key === "__typename") {
      return;
    }

    // Get the existing column data. If the column doesn't exist, it needs to
    // be pre-populated with nulls for all existing rows.
    const existing = ((rawTablesData[identifier] ?? {})[key] ??
      []) as PerspectiveColumn;
    const oldData = newKeys.has(key) ? Array(tableSize).fill(null) : existing;

    // If the update is missing this key, populate with an array of nulls.
    // Otherwise, this updated data will be appended as the new data for this
    // column.
    const newData = (
      missingKeys.has(key) ? Array(pagedSize).fill(null) : updatedTableData[key]
    ) as PerspectiveColumn;
    combined[key] = oldData.concat(newData);
  });
  rawTablesData[identifier] = combined;

  tables[identifier].update(updatedTableData);
  return difference(Object.keys(newSchema), Object.keys(schema));
}

/**
 * Copies a table schema and data to a secondary worker's memory
 * @param worker - Target worker where the table will be copied
 * @param identifier - String identifier to copy the table schema and data
 * @param schema - The schema for the table.
 * @param data - The data to populate the new table.
 */
export async function copyTable(
  worker: PerspectiveWorker,
  identifier: PerspectiveIdentifiers,
  schema: Record<string, string>,
  data: PerspectiveTableData | ArrayBuffer | undefined
): Promise<PerspectiveTable> {
  const table = await worker.table(schema);
  table.update(data || rawTablesData[identifier]);
  return table;
}

/**
 *
 * Replaces the data in a table with the given identifier.
 * @param identifier String identifier to replace the table data
 * @param data New data to be assigned to the table
 */
export function replaceTableData(
  identifier: PerspectiveIdentifiers,
  data: PerspectiveTableData
): void {
  tables[identifier].clear();
  tables[identifier].replace(data);
  rawTablesData[identifier] = data;
}

export default {
  attachTableTo,
  checkNewColumns,
  clearTable,
  clearPerspective,
  createTable,
  deleteTable,
  getTable,
  getTableData,
  recreateTable,
  updateTableData,
  copyTable,
  initWorkers,
  getSecondaryWorker,
  replaceTableData,
};
